import { cn } from '@/lib/utils'
import { Button } from 'global/components/Button'
import React from 'react'

interface IProps {
    children: React.ReactNode
}

export default class ErrorBoundary extends React.Component<IProps> {
    state = {
        error: undefined,
        errorInfo: undefined,
    } as {
        error: Error | undefined
        errorInfo: React.ErrorInfo | undefined
    }

    expandido = false
    copiado = false

    static getDerivedStateFromError(error: Error) {
        return { error }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error(error)
        this.setState({ error, errorInfo })
    }

    render() {
        if (!this.state.error) return this.props.children

        if (
            this.state.error.message &&
            this.state.error.message.includes(
                'Failed to fetch dynamically imported module',
            )
        ) {
            return (
                <div className="flex flex-col gap-4 items-center justify-center h-[calc(100vh-4rem)]">
                    <h1 className="text-3xl font-bold text-center">
                        Ocorreu um erro ao carregar a aplicação
                    </h1>
                    <h2 className="text-xl text-center">
                        Isso pode ter sido causado por uma atualização
                        automática. Tente recarregar a página.
                    </h2>
                    <Button
                        className="text-sm"
                        variant="contained"
                        onClick={() => window.location.reload()}
                    >
                        Recarregar
                    </Button>
                </div>
            )
        }

        return (
            <div className="flex flex-col gap-4 items-center justify-center h-[calc(100vh-4rem)]">
                <h1 className="text-3xl font-bold">
                    Ocorreu um erro inesperado
                </h1>
                <p
                    className="text-xl overflow-hidden text-ellipsis whitespace-nowrap max-w-[80%]"
                    title={this.state.error.toString()}
                >
                    {'message' in this.state.error
                        ? String(this.state.error.message)
                        : (this.state.error as Error).toString()}
                </p>
                <div
                    className={cn(
                        this.expandido ? 'flex' : 'hidden',
                        'flex-col justify-start items-start',
                        'bg-gray-600 rounded-lg p-4 w-fit',
                        'max-h-[calc(60vh-4rem)] max-w-[95%]',
                        'h-fit overflow-y-auto',
                    )}
                >
                    <Button
                        onClick={() => {
                            navigator.clipboard.writeText(
                                `Error: ${String(
                                    this.state.error,
                                )}\n\nStack trace:\n${
                                    this.state.errorInfo?.componentStack
                                }`,
                            )
                            this.copiado = true
                            this.forceUpdate()
                        }}
                        className={cn({ 'bg-green-600': this.copiado }, 'w-24')}
                    >
                        {this.copiado ? 'Copiado' : 'Copiar'}
                    </Button>
                    <p
                        className="h-full text-white leading-tight"
                        dangerouslySetInnerHTML={{
                            __html:
                                this.state.errorInfo?.componentStack
                                    ?.replace(/</g, '&lt;')
                                    ?.split('\n')
                                    ?.join('<br />') ?? '',
                        }}
                    />
                </div>
                <div className="flex flex-row gap-4">
                    <Button
                        variant="contained"
                        className="text-sm"
                        onClick={() => {
                            this.expandido = !this.expandido
                            this.forceUpdate()
                        }}
                    >
                        {this.expandido
                            ? 'Ocultar detalhes'
                            : 'Mostrar detalhes'}
                    </Button>
                    <Button
                        className="text-sm"
                        variant="contained"
                        onClick={() => window.location.reload()}
                    >
                        Recarregar
                    </Button>
                    <Button
                        className="text-sm"
                        variant="contained"
                        onClick={() => (window.location.href = '/')}
                    >
                        Voltar ao início
                    </Button>
                </div>
            </div>
        )
    }
}
